import React from 'react';
import { useLogger } from '@hooks';
import {
	Box,
	DividerLine,
	Spacer,
	Typography,
	useTheme,
	Image,
	ComposableTypography,
	Tooltip,
	ComposedElement,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { rem } from '@helpers';
import { Anything, EnterpriseSolutionDetail } from '@types';
import { useSelectedSolution } from '@store';
import { useEnterpriseSolutionHelper } from './enterpriseSolutions.helper';
import {
	NavRight,
	NavUp,
	NavDown,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system/dist/assets/MBCP/icons';
import { DoubleArrow, FileConditions } from '@icons';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { FormatHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { PdfViewerDrawer } from '../pdfViewerDrawer';
import { Icons } from '@bcp-nextgen-dx-component-factory/accolade-design-system/dist/assets/MBCP';

export interface EnterpriseSolutionsProps {
	solutions: EnterpriseSolutionDetail[];
}

export const EnterpriseSolutions: React.FC<EnterpriseSolutionsProps> = ({ solutions }) => {
	const logger = useLogger();
	const t = useTranslation();
	const { palette } = useTheme();
	const lastIndex = solutions.lastIndexOf(solutions[solutions.length - 1]);
	const solutionSelected = useSelectedSolution();

	const {
		resources,
		anchorRef,
		tooltipRef,
		generalConditionsDrawerName,
		base64Conditions,
		seeAllAdvantages,
		handleOnClickSelectSolution,
		handleChangeViewAdvantages,
		handleShowModalBenefit,
		handleGeneralConditions,
	} = useEnterpriseSolutionHelper();

	logger('Enter in Enterprise Solution', { solutions, solutionSelected });

	return (
		<Box styles={{ flexDirection: 'column', width: '100%' }}>
			<div style={{ flexShrink: 0 }}>
				<Spacer vertical={rem(16)} />
			</div>
			<Box
				styles={{
					width: '100%',
					flexDirection: 'column',
				}}
			>
				<Typography appearance="headline-200-02">{resources.title}</Typography>
				<div style={{ flexShrink: 0 }}>
					<Spacer vertical={rem(10)} />
				</div>
				<Box styles={{ alignItems: 'center' }}>
					<Typography appearance="body-100-02" alternativeStyleProperties={{ color: '#80828F' } as Anything}>
						{resources.subtitle}
					</Typography>
					<div style={{ flexShrink: 0 }}>
						<Spacer horizontal={rem(10)} />
					</div>
					<Tooltip
						description={{ text: resources.tooltip }}
						placement={'bottom'}
						closeOnPressOutside={false}
						ref={tooltipRef}
						anchorRef={anchorRef}
					/>
					<ComposedElement
						size={'xxSmall'}
						web={{
							mouseEvents: {
								onMouseEnter: (): void => {
									tooltipRef.current?.show();
								},
								onMouseLeave: (): void => {
									tooltipRef.current?.hide();
								},
							},
						}}
						ref={anchorRef}
						icon={{ source: Icons.Question, svgProps: { fill: palette.global.ui18 } }}
						container={{ styles: { backgroundColor: palette.global.ui9 } }}
					/>
				</Box>
			</Box>
			<div style={{ flexShrink: 0 }}>
				<Spacer vertical={rem(50)} />
			</div>
			<Box
				styles={{
					textAlign: 'center',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				{solutions.map((solution, i) => (
					<React.Fragment key={solution.id}>
						<Box
							styles={{
								flexDirection: 'column',
								alignItems: 'center',
								width: rem(236),
								height: solution.id === solutionSelected?.id ? '100%' : '100%' + rem(4.35),
								background: palette.global.ui18,
								borderRadius: '8px',
								marginTop: solution.id === solutionSelected?.id ? rem(0) : rem(4.35),
							}}
							onPress={() => handleOnClickSelectSolution(solution)}
						>
							{/* Border Card */}
							<Box
								styles={{
									width: '100%',
									height: rem(8.7),
									background: palette.global.ui18,
								}}
							>
								<Box
									styles={{
										width: '100%',
										height: solution.id === solutionSelected?.id ? rem(8.7) : rem(4.35),
										background:
											solution.id === solutionSelected?.id
												? palette.global.ui4
												: palette.global.ui1,
										borderRadius: '8px 8px 0px 0px',
									}}
								/>
							</Box>
							{/* Rest of Card */}
							<Box
								styles={{
									padding: '16px',
									flexDirection: 'column',
									width: '100%',
									height: '100%',
									/* TO PUT CARD SAME LEVEL 
										marginTop: plan.id === planId ? rem(0) : rem(4.35), 
									*/
								}}
							>
								<div style={{ flexShrink: 0 }}>
									<Spacer vertical={rem(12.3)} />
								</div>
								{/* Title */}
								<Box styles={{ flexDirection: 'column', alignItems: 'center' }}>
									<div style={{ width: rem(165), textAlign: 'center' }}>
										<Typography appearance="body-100-01">{resources.cardTitle}</Typography>
									</div>
								</Box>
								<div style={{ flexShrink: 0 }}>
									<Spacer vertical={rem(8)} />
								</div>
								{/* Solution Name */}
								<Box styles={{ flexDirection: 'column', alignItems: 'center' }}>
									<div style={{ width: rem(165), height: rem(31), textAlign: 'center' }}>
										<Typography
											appearance="microcopy-75-04"
											alternativeStyleProperties={{ color: palette.global.ui1 } as Anything}
										>
											{t('placeholders.enterpriseSolution.card.subtitle', {
												planName: solution.name,
											})}
										</Typography>
									</div>
								</Box>
								<div style={{ flexShrink: 0 }}>
									<Spacer vertical={rem(24)} />
								</div>
								{/* Amounts */}
								<Box styles={{ flexDirection: 'column', alignItems: 'center' }}>
									<ComposableTypography
										text={t('placeholders.enterpriseSolution.card.genericValue', {
											monthlyAmount: FormatHelper.formatNumber(solution.monthlyAmount, 'amount'),
											suffix: t('general.eur').toUpperCase(),
										})}
										baseProps={{
											appearance: 'microcopy-75-01',
											alternativeStyleProperties: {
												color: palette.global.ui60,
											} as Anything,
										}}
										tags={{
											highlight: {
												appearance: 'subtitle-100-03',
												alternativeStyleProperties: {
													color: palette.global.ui2,
												} as Anything,
											},
											highlight2: {
												appearance: 'microcopy-100-02',
												alternativeStyleProperties: {
													color: palette.global.ui2,
												} as Anything,
											},
										}}
									/>
									<DoubleArrow />
									<ComposableTypography
										text={t('placeholders.enterpriseSolution.card.genericValue', {
											monthlyAmount: FormatHelper.formatNumber(
												solution.monthlyAmountDiscount,
												'amount'
											),
											suffix: t('general.eur').toUpperCase(),
										})}
										baseProps={{
											appearance: 'microcopy-75-01',
											alternativeStyleProperties: {
												color: palette.global.ui1,
											} as Anything,
										}}
										tags={{
											highlight: {
												appearance: 'subtitle-100-03',
												alternativeStyleProperties: {
													color: palette.global.ui1,
												} as Anything,
											},
											highlight2: {
												appearance: 'microcopy-100-02',
												alternativeStyleProperties: {
													color: palette.global.ui1,
												} as Anything,
											},
										}}
									/>
									<ComposableTypography
										text={t('placeholders.enterpriseSolution.card.taxMonths', {
											taxMonths: solution.discountDurationMonths,
										})}
										baseProps={{
											appearance: 'microcopy-25-01',
											alternativeStyleProperties: {
												color: palette.global.ui2,
											} as Anything,
										}}
										tags={{
											highlight: {
												appearance: 'microcopy-25-03',
												alternativeStyleProperties: {
													color: palette.global.ui2,
												} as Anything,
											},
										}}
									/>
								</Box>
								<div style={{ flexShrink: 0 }}>
									<Spacer vertical={rem(24)} />
								</div>
								{/* Tax */}
								<Box styles={{ flexDirection: 'column', alignItems: 'center' }}>
									<div style={{ width: rem(165), height: rem(31), textAlign: 'center' }}>
										<Typography
											appearance="microcopy-75-01"
											alternativeStyleProperties={{ color: palette.global.ui60 } as Anything}
										>
											{resources.addStampDuty}
										</Typography>
									</div>
								</Box>
								<div style={{ flexShrink: 0 }}>
									<Spacer vertical={rem(24)} />
								</div>
								{/* Chip benefits */}
								<Box styles={{ justifyContent: 'center' }}>
									<Box
										styles={{
											flexDirection: 'column',
											alignItems: 'center',
										}}
									>
										<div
											style={{
												padding: '6px 16px 5px',
												background: '#F5F6FA',
												borderRadius: '100px',
												textAlign: 'center',
											}}
											onClick={handleShowModalBenefit}
										>
											<Typography appearance="microcopy-75-03">
												{t('placeholders.enterpriseSolution.card.benefit', {
													money: solution.benefit,
													suffix: t('general.euro'),
												}).toUpperCase()}
											</Typography>
										</div>
									</Box>
									<Box
										styles={{
											alignItems: 'center',
										}}
										onPress={handleShowModalBenefit}
									>
										<Image
											source={NavRight}
											svgProps={{
												fill: palette.global.ui2,
												width: rem(18),
												height: rem(18),
											}}
										/>
									</Box>
								</Box>
								{/*TODO: analysis this code*/}
								<div style={{ flexShrink: 0 }}>
									<Spacer vertical={rem(24)} />
								</div>
								<div style={{ flexShrink: 0 }}>
									<DividerLine />
								</div>
								<div style={{ flexShrink: 0 }}>
									<Spacer vertical={rem(12)} />
								</div>
								{/* Conditions of solution */}
								<div
									style={{
										maxHeight: seeAllAdvantages ? '100%' : rem(132),
										overflowY: seeAllAdvantages ? 'unset' : 'hidden',
									}}
								>
									{solution.conditions.map((condition) => {
										return (
											<>
												<Box
													styles={{
														flexDirection: 'column',
														alignItems: 'center',
													}}
												>
													<div
														style={{
															width: rem(204),
															textAlign: 'center',
															justifyContent: 'center',
														}}
													>
														<div
															style={{
																justifyContent: 'center',
															}}
														>
															<ComposableTypography
																text={condition.title}
																baseProps={{
																	appearance: 'microcopy-75-02',
																	alternativeStyleProperties: {
																		color: palette.global.ui2,
																	} as Anything,
																}}
																tags={{
																	highlight: {
																		appearance: 'microcopy-75-04',
																		alternativeStyleProperties: {
																			color: palette.global.ui2,
																		} as Anything,
																	},
																}}
															/>
														</div>
														<Spacer vertical={rem(4)} />
														<Typography
															appearance="microcopy-75-02"
															alternativeStyleProperties={
																{ color: palette.global.ui60 } as Anything
															}
														>
															{condition.subtitle}
														</Typography>
													</div>
												</Box>
												<div style={{ flexShrink: 0 }}>
													<Spacer vertical={rem(12)} />
												</div>
												<div style={{ flexShrink: 0 }}>
													<DividerLine />
												</div>
												<div style={{ flexShrink: 0 }}>
													<Spacer vertical={rem(12)} />
												</div>
											</>
										);
									})}
								</div>
								{/* General Conditions */}
								<div style={{ alignItems: 'center' }}>
									<Box
										styles={{ flexDirection: 'row', height: '100%', justifyContent: 'center' }}
										onPress={handleGeneralConditions}
									>
										<FileConditions />
										<Box styles={{ alignItems: 'center' }}>
											<Typography appearance="microcopy-75-02" colorsLevel="linkDefault">
												{resources.generalConditions}
											</Typography>
										</Box>
									</Box>
								</div>
							</Box>
						</Box>
						{solutions.length > 1 && lastIndex !== i && <Spacer horizontal={rem(24)} />}
					</React.Fragment>
				))}
			</Box>
			<div style={{ flexShrink: 0 }}>
				<Spacer vertical={rem(24)} />
			</div>
			{/* Show or Hide All Advantages */}
			<Box
				styles={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', height: '100%' }}
				onPress={() => handleChangeViewAdvantages(seeAllAdvantages)}
			>
				<div style={{ textAlign: 'center' }}>
					<Typography
						appearance="body-100-02"
						alternativeStyleProperties={{ color: palette.global.ui9 } as Anything}
					>
						{seeAllAdvantages ? resources.closeAdvantages : resources.openAdvantages}
					</Typography>
				</div>
				<Box
					styles={{
						alignItems: 'center',
						height: '100%',
					}}
				>
					<Image
						source={seeAllAdvantages ? NavUp : NavDown}
						svgProps={{
							fill: palette.global.ui2,
							width: rem(18),
							height: rem(18),
						}}
					/>
				</Box>
			</Box>
			<div style={{ flexShrink: 0 }}>
				<Spacer vertical={rem(48)} />
			</div>
			{/* Text Publicity */}
			<Box styles={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}>
				<ComposableTypography
					text={resources.publicityPhrase}
					baseProps={{
						appearance: 'microcopy-25-01',
						alternativeStyleProperties: {
							color: palette.global.ui60,
						} as Anything,
					}}
					tags={{
						highlight: {
							appearance: 'microcopy-25-03',
							alternativeStyleProperties: {
								color: palette.global.ui60,
							} as Anything,
						},
					}}
				/>
			</Box>
			{/* Drawer With Document General Conditions */}
			{base64Conditions && (
				<PdfViewerDrawer
					title={t('placeholders.enterpriseSolution.generalConditions.title', {
						planName: solutionSelected?.name,
					})}
					subtitle={resources.attentionRead}
					base64={base64Conditions}
					drawerUniqueName={generalConditionsDrawerName}
					handleOnClickAcceptTerms={() => void 0}
					wasAccepted={true}
				/>
			)}
		</Box>
	);
};
